import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/map");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left form */}
      <div className="flex flex-col px-6 py-10 md:px-16 bg-white">
        <Link to="/" data-testid="login-logo-link" className="flex items-center gap-2.5 mb-16">
          <img
            src="/brand/ctc-logo-240.png"
            alt="Coast To Coast Permits"
            className="h-14 w-auto object-contain"
            width="45"
            height="56"
          />
          <span className="font-outfit font-bold text-slate-900 text-base">Coast To Coast Permitting</span>
        </Link>

        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">Welcome back</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-slate-900 mb-3">Sign in</h1>
          <p className="text-slate-600 mb-10">Access the permitting map for all 3,221 U.S. counties.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                placeholder="you@company.com"
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                placeholder="••••••••"
                className="mt-2 h-11"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
          </form>

          <div className="mt-8 text-sm text-slate-600">
            New here?{" "}
            <Link to="/register" data-testid="link-to-register" className="font-medium text-slate-900 hover:underline">
              Create an account
            </Link>
          </div>
        </div>
      </div>

      {/* Right visual */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/4170184/pexels-photo-4170184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Construction site"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-900/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-16 text-white">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-300 mb-3">For permitting pros</div>
          <h2 className="font-outfit text-4xl font-semibold tracking-tight max-w-md">
            Stop chasing 50 different state portals.
          </h2>
          <p className="mt-4 text-slate-200 max-w-md">
            One map, every county, every permit type. Built by people who've waited too long for permit approvals.
          </p>
        </div>
      </div>
    </div>
  );
}
